'use client'

import PlaceBidLabel from '@/components/offers/place-bid-label'
import React, { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import classNames from 'classnames'
import { ChevronRight, ShoppingCart } from 'lucide-react'
import InstantSell from '@/components/offers/instant-sell'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import BitcoinIcon from '@/icons/bitcoin-filled'
import { formatActivityBtc } from '@/utils/format-activity-btc'
import { formatActivityUsd } from '@/utils/format-activity-usd'
import useIsHydrated from '@/utils/hooks/useIsHydrated'
import {
	percentOfFloor,
	type BidActivity,
	type BidListing,
	type CollectionBid,
	type InscriptionBidGroup
} from '@/utils/bids'
import BidAmount from './bid-amount'
import CollectionArt from './collection-art'
import InscriptionThumb from './inscription-thumb'
import { quickViewPayload } from './quick-view'

function timeAgo(at: number, now: number): string {
	const s = Math.max(0, Math.round((now - at) / 1000))
	if (s < 45) return 'now'
	const m = Math.round(s / 60)
	if (m < 60) return `${m}m`
	const h = Math.round(m / 60)
	if (h < 24) return `${h}h`
	return `${Math.round(h / 24)}d`
}

function useNow(interval = 15_000) {
	const [now, setNow] = useState(() => Date.now())
	useEffect(() => {
		const t = setInterval(() => setNow(Date.now()), interval)
		return () => clearInterval(t)
	}, [interval])
	return now
}

const segmentClass = (active: boolean) =>
	classNames(
		'h-7 flex-1 rounded-md text-xs font-medium transition-colors',
		active
			? 'bg-background text-foreground shadow-sm'
			: 'text-muted-foreground hover:text-foreground'
	)

export function TopBidsRail({
	collectionBids,
	groups,
	listings,
	selectedSlug,
	onSelect,
	onPlaceBid,
	biddingOpen
}: {
	collectionBids: CollectionBid[]
	groups: InscriptionBidGroup[]
	listings: Record<string, BidListing[]>
	selectedSlug: string | null
	onSelect: (slug: string) => void
	onPlaceBid: (slug: string) => void
	biddingOpen: boolean
}) {
	const [view, setView] = useState<'collections' | 'items'>('collections')
	const openInscription = useInscriptionModal()

	// Best bid per collection, with the depth of its book.
	const book = useMemo(() => {
		const bySlug = new Map<string, { best: CollectionBid; depth: number; qty: number }>()
		for (const bid of collectionBids) {
			const row = bySlug.get(bid.slug)
			if (!row) bySlug.set(bid.slug, { best: bid, depth: 1, qty: bid.quantity })
			else {
				row.depth += 1
				row.qty += bid.quantity
				if (bid.priceSats > row.best.priceSats) row.best = bid
			}
		}
		return [...bySlug.values()].sort((a, b) => b.best.priceSats - a.best.priceSats)
	}, [collectionBids])
	const items = useMemo(
		() => groups.flatMap((g) => g.bids).sort((a, b) => b.priceSats - a.priceSats),
		[groups]
	)
	const selected = book.find((r) => r.best.slug === selectedSlug) ?? book[0]
	const floorListing = selected ? listings[selected.best.slug]?.[0] : undefined
	const floorSats = selected?.best.floorSats ?? floorListing?.satoshi_price ?? null
	const pct = selected ? percentOfFloor(selected.best.priceSats, floorSats) : null

	return (
		<div className="flex h-full min-h-0 flex-col" data-testid="bids-top-rail">
			<div className="shrink-0 border-b border-border p-2">
				<div className="flex rounded-lg bg-muted p-0.5" role="tablist" aria-label="Top offers">
					<button
						type="button"
						role="tab"
						aria-selected={view === 'collections'}
						onClick={() => setView('collections')}
						className={segmentClass(view === 'collections')}
					>
						Collections
					</button>
					<button
						type="button"
						role="tab"
						aria-selected={view === 'items'}
						onClick={() => setView('items')}
						className={segmentClass(view === 'items')}
					>
						Inscriptions
					</button>
				</div>
			</div>
			<div className="flex shrink-0 items-center justify-between px-4 py-2 text-[11px] text-muted-foreground">
				<span>{view === 'collections' ? 'Collection' : 'Item'}</span>
				<span>{view === 'collections' ? 'Top offer · Floor' : 'Offer · Listed'}</span>
			</div>
			<div className="min-h-0 flex-1 overflow-y-auto">
				{(view === 'collections' ? book.length === 0 : items.length === 0) && (
					<p className="px-4 py-8 text-center text-xs text-muted-foreground" role="status">
						{view === 'collections'
							? 'No collection offers yet. Every collection page has a Make an offer tile.'
							: 'No inscription offers yet. Any inscription page takes an offer.'}
					</p>
				)}
				{view === 'collections'
					? book.map(({ best, depth, qty }) => {
							const rowPct = percentOfFloor(best.priceSats, best.floorSats)
							const active = selected?.best.slug === best.slug
							return (
								<button
									key={best.slug}
									type="button"
									onClick={() => onSelect(best.slug)}
									aria-pressed={active}
									className={classNames(
										'flex w-full items-center gap-2.5 border-l-2 px-3 py-2 text-left transition-colors hover:bg-accent',
										active ? 'border-[#9E77ED] bg-accent/60' : 'border-transparent'
									)}
								>
									<span className="relative size-8 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
										<CollectionArt collection={best.collection} />
									</span>
									<span className="flex min-w-0 flex-1 flex-col gap-0.5">
										<span className="block truncate text-xs leading-4 font-semibold text-foreground">
											{best.collection.name}
										</span>
										<span className="block text-[11px] leading-4 font-normal text-muted-foreground">
											{qty} offer{qty === 1 ? '' : 's'} · {depth} level{depth === 1 ? '' : 's'}
										</span>
									</span>
									<span className="flex shrink-0 flex-col items-end gap-0.5">
										<BidAmount
											sats={best.priceSats}
											className="text-xs leading-4 font-semibold text-foreground"
										/>
										<span className="text-[11px] leading-4 font-normal text-muted-foreground">
											{rowPct !== null ? `${Math.abs(100 - rowPct)}% ${rowPct > 100 ? 'Above' : 'Below'} Floor` : '—'}
										</span>
									</span>
								</button>
							)
					  })
					: items.map((bid) => (
							<button
								key={bid.id}
								type="button"
								onClick={(e) =>
									openInscription(quickViewPayload(bid.inscriptionId, bid.collection), e)
								}
								className="flex w-full items-center gap-2.5 border-l-2 border-transparent px-3 py-2 text-left transition-colors hover:bg-accent"
							>
								<span className="relative size-8 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
									<InscriptionThumb id={bid.inscriptionId} collection={bid.collection} />
								</span>
								<span className="flex min-w-0 flex-1 flex-col gap-0.5">
									<span className="block truncate text-xs leading-4 font-semibold text-foreground">
										{bid.name}
									</span>
									<span className="block truncate text-[11px] leading-4 font-normal text-muted-foreground">
										{bid.collection.name}
									</span>
								</span>
								<span className="flex shrink-0 flex-col items-end gap-0.5">
									<BidAmount
										sats={bid.priceSats}
										className="text-xs leading-4 font-semibold text-foreground"
									/>
									{bid.listedSats ? (
										<BidAmount
											sats={bid.listedSats}
											className="text-[11px] leading-4 font-normal text-muted-foreground"
											iconClassName="size-2.5"
										/>
									) : <span className="text-[11px] leading-4 font-normal text-muted-foreground">—</span>}
								</span>
							</button>
					  ))}
			</div>
			{selected && view === 'collections' && (
				<div
					className="shrink-0 border-t border-border bg-card p-3"
					data-testid="bids-selected-card"
				>
					<div className="flex items-center gap-2.5">
						<span className="relative size-10 shrink-0 overflow-hidden rounded-lg border border-border bg-muted">
							<CollectionArt collection={selected.best.collection} />
						</span>
						<div className="min-w-0 flex-1">
							<Link
								href={`/collection/${selected.best.slug}`}
								className="flex items-center gap-1 truncate text-sm font-semibold text-foreground hover:underline"
							>
								<span className="truncate">{selected.best.collection.name}</span>
								<ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
							</Link>
							<div className="text-[11px] text-muted-foreground">
								{pct !== null ? `Top offer is ${Math.abs(100 - pct)}% ${pct > 100 ? 'Above' : 'Below'} Floor` : 'Collection offer'}
							</div>
						</div>
					</div>
					<div className="mt-3 grid grid-cols-2 gap-2">
						<button
							type="button"
							onClick={() => onPlaceBid(selected.best.slug)}
							disabled={!biddingOpen}
							data-testid="bids-place-bid"
							title={biddingOpen ? undefined : 'Offers open soon'}
							className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-500 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
						>
							{biddingOpen ? <PlaceBidLabel /> : 'Offers soon'}
						</button>
						{floorListing ? (
							<button
								type="button"
								data-testid="bids-buy-floor"
								onClick={(e) =>
									openInscription(
										quickViewPayload(floorListing.inscription_id, selected.best.collection),
										e
									)
								}
								className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border text-xs font-semibold text-foreground transition-colors hover:bg-accent"
							>
								<ShoppingCart className="size-3.5" />
								Buy floor
							</button>
						) : (
							<Link
								href={`/collection/${selected.best.slug}`}
								className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border text-xs font-semibold text-foreground transition-colors hover:bg-accent"
							>
								<ShoppingCart className="size-3.5" />
								Browse
							</Link>
						)}
					</div>
					{biddingOpen ? (
						<div className="mt-2 [&:empty]:hidden">
							<InstantSell slug={selected.best.slug} name={selected.best.collection.name} compact showAmount />
						</div>
					) : null}
				</div>
			)}
		</div>
	)
}

/** Plain coloured text in the stream: purple for a bid, green for a fill (with ▲ on a raise, never colour alone), muted otherwise. */
const KIND_TEXT: Record<BidActivity['kind'], string> = {
	placed: 'text-[#6941C6] dark:text-[#B692F6]',
	raised: 'text-success-ink',
	filled: 'text-emerald-600 dark:text-emerald-400',
	cancelled: 'text-muted-foreground'
}

const KIND_COPY: Record<BidActivity['kind'], string> = {
	placed: 'Offer',
	raised: 'Raised',
	filled: 'Filled',
	cancelled: 'Cancelled'
}

export function BidActivityRail({
	activity,
	onClose
}: {
	activity: BidActivity[]
	onClose?: () => void
}) {
	return (
		<div className="flex h-full min-h-0 flex-col" data-testid="bids-activity-rail">
			{onClose ? (
				<button
					type="button"
					onClick={onClose}
					aria-label="Hide offer activity panel"
					className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3 transition-colors hover:bg-accent"
				>
					<span className="text-sm font-bold">Offer Activity</span>
					<ChevronRight className="size-5 text-muted-foreground" />
				</button>
			) : (
				<div className="shrink-0 border-b border-border px-4 py-3 text-sm font-bold">
					Offer Activity
				</div>
			)}
			<BidActivityList activity={activity} />
		</div>
	)
}

/**
 * The stream, in the home page's Trades-feed shape: when, what, how much.
 * Newest first.
 */
export function BidActivityList({ activity }: { activity: BidActivity[] }) {
	const now = useNow()
	const hydrated = useIsHydrated()
	const router = useRouter()
	const openInscription = useInscriptionModal()
	const { exchangeRate } = useExchangeRate()
	return (
		<ol className="min-h-0 flex-1 overflow-y-auto">
			{activity.map((a, i) => {
				const usd =
					Number.isFinite(exchangeRate) && exchangeRate > 0
						? formatActivityUsd((a.priceSats / 1e8) * exchangeRate)
						: null
				return (
					<li
						key={a.id}
						data-new-trade={i === 0 && now - a.at < 10_000 ? 'true' : undefined}
						role="button"
						tabIndex={0}
						onClick={(e) =>
							a.inscriptionId
								? openInscription(quickViewPayload(a.inscriptionId, a.collection), e)
								: router.push(`/collection/${a.slug}`)
						}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault()
								a.inscriptionId
									? openInscription(quickViewPayload(a.inscriptionId, a.collection), e)
									: router.push(`/collection/${a.slug}`)
							}
						}}
						className="my-px grid min-h-11 cursor-pointer grid-cols-[36px_minmax(0,1fr)_max-content] items-center whitespace-nowrap transition-all hover:bg-accent/50 sm:min-h-0"
					>
						<div suppressHydrationWarning className="py-1 pl-3 text-left text-xs text-muted-foreground">
							{hydrated ? timeAgo(a.at, now) : '\u00A0'}
						</div>
						<div className="flex min-w-0 items-center gap-x-1.5 overflow-hidden p-1 text-left text-xs">
							<span className="relative size-4 min-h-[1rem] min-w-[1rem] shrink-0 overflow-hidden rounded-sm bg-muted">
								{a.inscriptionId ? (
									<InscriptionThumb id={a.inscriptionId} collection={a.collection} />
								) : (
									<CollectionArt collection={a.collection} />
								)}
							</span>
							<span className="min-w-0 truncate">{a.name}</span>
							<span
								className={classNames(
									'inline-flex shrink-0 items-center text-[10px] font-semibold leading-[14px]',
									KIND_TEXT[a.kind]
								)}
								title={a.bidder}
							>
								{a.kind === 'raised' && <span aria-hidden="true">▲ </span>}
								{a.label ?? KIND_COPY[a.kind]}
							</span>
						</div>
						<div className="ml-3 py-1 pl-0 pr-3 text-right text-xs sm:pr-4">
							<div className="flex items-center justify-end gap-0.5 whitespace-nowrap">
								<BitcoinIcon className="relative -top-[0.5px] size-3 shrink-0" />
								<span className="tabular-nums whitespace-nowrap">
									{formatActivityBtc(a.priceSats)}
									{usd && (
										<span className="block text-[10px] text-muted-foreground sm:ml-0.5 sm:inline">
											(${usd})
										</span>
									)}
								</span>
							</div>
						</div>
					</li>
				)
			})}
		</ol>
	)
}
